// How footnotes look and are edited.
//
// A reference in the body shows a raised number (1, 2, … - not `[^label]`);
// pressing it opens a dialog that edits the definition as it looks. The
// definitions are gathered at the end of the body (the editor's
// `FootnoteBlocks`) and show the same numbers.
//
// Where the number comes from: `FootnoteBlocks` hands it out as a node
// decoration. ProseMirror puts a node decoration's `attrs` on the node view's
// outer `dom` (the `react-renderer` element `@tiptap/react` makes), so they
// reach neither `NodeViewProps.HTMLAttributes` nor what `NodeViewWrapper`
// draws. React reads `NodeViewProps.decorations` through `footnoteNumberOf`
// (the number is in the decoration's spec) and writes `data-n` /
// `data-missing` itself. No MutationObserver peeking at DOM attributes.
//
// Redrawing when only the number changes: `ReactNodeView.update()` skips
// updating props when the node is the same instance, so adding, removing or
// reordering definitions changes a reference's number without a redraw. The
// `update` option compares decorations too (`markChanged`), and the same path
// carries "is it referenced".
import type {
  AnyExtension,
  Editor,
  JSONContent,
  NodeViewProps,
} from "@tiptap/core";
import { Extension } from "@tiptap/core";
import {
  EditorContent,
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
} from "@tiptap/react";
import {
  blockIdDomAttrs,
  createEditorExtensions,
  DOCUMENT_BODY_CLASS,
  FootnoteDef,
  FootnoteRef,
  findFootnoteDef,
  footnoteNumberOf,
  footnoteOrder,
  footnoteReferencedOf,
} from "@trameria/editor";
import { m } from "@trameria/i18n";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { ICON_BUTTON } from "../../ui/button";
import { Icon } from "../../ui/icon";
import { Modal } from "../../ui/modal";
import { separateUndo } from "./editor-steps";
import { ignoreMutationForReactNodeView } from "./react-node-view-mutation";

/** Where the footnote dialog is opened from (node views -> the dialog's host). */
export interface FootnoteModalStorage {
  /** The label being edited, or null. */
  current(): string | null;
  show(label: string): void;
  hide(): void;
  subscribe(listener: () => void): () => void;
}

declare module "@tiptap/core" {
  interface Storage {
    footnoteRef?: Partial<FootnoteModalStorage>;
  }
}

/**
 * Adds a definition with one empty paragraph at the end of the body (one
 * transaction). Where it finally goes is `FootnoteBlocks`'s job: reference
 * order, before a trailing empty paragraph (which is never written to
 * markdown).
 *
 * Between undo boundaries: otherwise it would merge with the keystrokes right
 * after, and one undo would take both the typing and the definition.
 */
export function appendEmptyFootnoteDef(editor: Editor, label: string): void {
  const { schema, state } = editor;
  separateUndo(editor);
  editor.view.dispatch(
    state.tr.insert(
      state.doc.content.size,
      schema.nodes.footnoteDef.create(
        { label },
        schema.nodes.paragraph.create(),
      ),
    ),
  );
  separateUndo(editor);
}

/**
 * Pressing a reference: a missing definition is made first, then the dialog
 * opens. Making it on the spot rescues stray references; opening without one
 * would be a dialog whose typing can never be written back.
 */
export function openFootnoteEditor(editor: Editor, label: string): void {
  if (findFootnoteDef(editor.state.doc, label) === null) {
    appendEmptyFootnoteDef(editor, label);
  }
  editor.storage.footnoteRef?.show?.(label);
}

/** How long typing must pause before it is written back to the body. */
export const FOOTNOTE_WRITE_DEBOUNCE_MS = 500;

/** The class that highlights the reference jumped to (`@keyframes trameria-footnote-flash`). */
export const FOOTNOTE_FLASH_CLASS = "trameria-footnote-ref-flash";
/** How long the highlight lasts (ms), as the CSS animation. */
export const FOOTNOTE_FLASH_MS = 1000;

/**
 * Jumps to the first reference of `label` in the body and highlights it for a
 * second: where the mark of a definition at the end goes. A definition's text
 * is typed in place, so a dialog over it is not needed; getting back to where
 * it is referenced is. Nothing happens without a reference.
 *
 * Looked up in the DOM: a document walk would end in the same DOM lookup to
 * scroll it into view.
 */
export function scrollToFootnoteRef(editor: Editor, label: string): boolean {
  const refs = editor.view.dom.querySelectorAll<HTMLElement>(
    "sup[data-footnote-ref]",
  );
  const target = [...refs].find((ref) => ref.dataset.label === label);
  if (!target) return false;
  // Not in happy-dom.
  if (typeof target.scrollIntoView === "function") {
    target.scrollIntoView({ block: "center", behavior: "smooth" });
  }
  target.classList.add(FOOTNOTE_FLASH_CLASS);
  setTimeout(
    () => target.classList.remove(FOOTNOTE_FLASH_CLASS),
    FOOTNOTE_FLASH_MS,
  );
  return true;
}

/**
 * Whether the marks (number, referenced) changed. Compared by content:
 * decorations are rebuilt on every document change, so comparing instances
 * would redraw every reference on every keystroke. "Referenced" is compared
 * too: removing the last reference changes neither the definition node nor
 * its number.
 */
function markChanged(
  before: readonly { spec?: unknown }[],
  after: readonly { spec?: unknown }[],
): boolean {
  return (
    footnoteNumberOf(before) !== footnoteNumberOf(after) ||
    footnoteReferencedOf(before) !== footnoteReferencedOf(after)
  );
}

/** Redraws React only when the node or its marks changed. */
function redrawWhenMarkChanges({
  oldNode,
  newNode,
  oldDecorations,
  newDecorations,
  updateProps,
}: {
  oldNode: unknown;
  newNode: unknown;
  oldDecorations: readonly { spec?: unknown }[];
  newDecorations: readonly { spec?: unknown }[];
  updateProps: () => void;
}): boolean {
  if (oldNode !== newNode || markChanged(oldDecorations, newDecorations)) {
    updateProps();
  }
  return true;
}

// --- the reference (a raised number in the body) -----------------------------

export function FootnoteRefView({ node, editor, decorations }: NodeViewProps) {
  const label = String(node.attrs.label ?? "");
  const n = footnoteNumberOf(decorations);
  return (
    <NodeViewWrapper
      as="sup"
      className="trameria-footnote-ref"
      data-footnote-ref="true"
      data-label={label}
      data-n={n === null ? undefined : String(n)}
      data-missing={n === null ? "true" : undefined}
    >
      <button
        type="button"
        // A reference without a definition makes one when pressed; its name says so.
        aria-label={
          n === null
            ? m.blocks_footnote_create({ label })
            : m.blocks_footnote_named({ n })
        }
        // Keeps the body's selection.
        onMouseDown={(e) => e.preventDefault()}
        // The focus moves here on press (mousedown is stopped, so it would
        // not): the dialog returns the focus to what was focused when it opened.
        onClick={(e) => {
          e.currentTarget.focus();
          openFootnoteEditor(editor, label);
        }}
      >
        {n === null ? `[^${label}]` : String(n)}
      </button>
    </NodeViewWrapper>
  );
}

/**
 * The editor's footnote reference with this node view. The storage holds the
 * dialog's state; the host (`FootnoteModalHost`) subscribes to it.
 */
export const FootnoteRefWithView = FootnoteRef.extend<
  Record<string, never>,
  FootnoteModalStorage
>({
  addStorage() {
    // TipTap hands out a shallow copy of this object as `editor.storage`, so
    // the state lives in the closure and is read through `current()`.
    const listeners = new Set<() => void>();
    let open: string | null = null;
    const storage: FootnoteModalStorage = {
      current: () => open,
      show(label) {
        open = label;
        for (const l of listeners) l();
      },
      hide() {
        open = null;
        for (const l of listeners) l();
      },
      subscribe(listener) {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
    };
    return storage;
  },

  addNodeView() {
    return ReactNodeViewRenderer(FootnoteRefView, {
      update: redrawWhenMarkChanges,
    });
  },
});

// --- the definition (blocks at the end of the body) ----------------------------

export function FootnoteDefView({ node, editor, decorations }: NodeViewProps) {
  const label = String(node.attrs.label ?? "");
  const n = footnoteNumberOf(decorations);
  // There is always a number (`footnoteOrder` numbers every definition under
  // the doc); null only without `FootnoteBlocks`, where the label shows and
  // nothing can be jumped to.
  const name = n === null ? label : String(n);
  const mark = n === null ? label : `${n}.`;
  // A definition nobody references has nowhere to jump: not pressable
  // (a button calling itself "to the reference" doing nothing).
  const referenced = footnoteReferencedOf(decorations);
  return (
    // The block id is set here (node views do not go through renderHTML).
    <NodeViewWrapper
      as="div"
      data-footnote-def={label}
      data-n={n === null ? undefined : String(n)}
      {...blockIdDomAttrs(node)}
    >
      {/* The mark is the body's number (`1.`; `[^1]` was markdown showing
          through). Pressing it jumps to the reference: the text is typed in
          place, so no dialog on top. Unreferenced: `disabled` (same text and
          box, only the pressable cue drops; swapping the element would move
          the text by the padding). */}
      <button
        type="button"
        className="trameria-footnote-label"
        aria-label={
          referenced
            ? m.blocks_footnote_to_ref({ name })
            : m.blocks_footnote_unreferenced({ name })
        }
        disabled={!referenced}
        contentEditable={false}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => scrollToFootnoteRef(editor, label)}
      >
        {mark}
      </button>
      <NodeViewContent className="trameria-footnote-body" />
    </NodeViewWrapper>
  );
}

export const FootnoteDefWithView = FootnoteDef.extend({
  addNodeView() {
    // ProseMirror replaces the content itself; React redraws only for marks.
    return ReactNodeViewRenderer(FootnoteDefView, {
      update: redrawWhenMarkChanges,
      // Keeps iPhones from freezing when a definition is inserted
      // (react-node-view-mutation.ts).
      ignoreMutation: ignoreMutationForReactNodeView(this.editor),
    });
  },
});

// --- the dialog ---------------------------------------------------------------

/**
 * Escape inside the dialog's editor closes the dialog. The Modal listens for
 * Escape on its panel, but prosemirror-view handles Escape inside a
 * contenteditable before it gets there; a keymap runs first.
 */
const CloseOnEscape = Extension.create<{ onEscape: () => boolean }>({
  name: "trameriaFootnoteCloseOnEscape",
  addOptions() {
    return { onEscape: () => false };
  },
  addKeyboardShortcuts() {
    return { Escape: () => this.options.onEscape() };
  },
});

const NO_LABEL = () => null;
const NO_SUBSCRIBE = () => () => {};

/**
 * Hosts the footnote dialog of one editor: mounted once next to the body. The
 * node views open it through the reference's storage.
 */
export function FootnoteModalHost({
  editor,
  extensions,
}: {
  editor: Editor | null;
  /** The display swaps the dialog's editor uses (the same look as the body). */
  extensions: () => AnyExtension[];
}) {
  const storage = editor?.storage.footnoteRef;
  const label = useSyncExternalStore(
    storage?.subscribe ?? NO_SUBSCRIBE,
    () => storage?.current?.() ?? NO_LABEL(),
    NO_LABEL,
  );
  if (!editor || label === null) return null;
  return (
    <FootnoteModal
      // A new dialog per label (state is not carried between footnotes).
      key={label}
      editor={editor}
      label={label}
      extensions={extensions}
      onClose={() => storage?.hide?.()}
    />
  );
}

/**
 * Edits a definition as it looks, in a dialog.
 *
 * Inside is a separate, non-collaborative editor; it is written back when
 * typing pauses for 500ms and when the dialog closes - not per keystroke
 * through Yjs (like the table's width grips).
 *
 * The place to write is looked up by label every time (`findFootnoteDef`): the
 * body moves while the dialog is open (others' edits, `FootnoteBlocks`
 * reordering), and a remembered position would overwrite another block. When
 * it is not found, the definition is gone: nothing is written and the dialog
 * says so.
 *
 * Undo boundaries before and after a write (the undo manager merges changes
 * within 500ms, so without them it would undo with the surrounding typing).
 *
 * Closed by Escape, the backdrop and the close button.
 *
 * Known limit: the inner editor is not collaborative, so a write replaces the
 * whole definition. Someone else editing the same definition while the dialog
 * is open loses that edit every time this side pauses typing, and their caret
 * jumps. `findFootnoteDef` returns the first definition of a label, so with
 * duplicated definitions (two clients reordering at once) only one is written.
 * Fixing that means putting the inner editor on a Yjs sub-document; not now.
 */
export function FootnoteModal({
  editor,
  label,
  extensions,
  onClose,
}: {
  editor: Editor;
  label: string;
  extensions: () => AnyExtension[];
  onClose: () => void;
}) {
  // The content when opened. Later changes are not pushed in (no props ->
  // state copies); closing and opening again reads it anew.
  const initial = useMemo<JSONContent[]>(() => {
    const hit = findFootnoteDef(editor.state.doc, label);
    const content = hit?.node.toJSON().content as JSONContent[] | undefined;
    return content && content.length > 0 ? content : [{ type: "paragraph" }];
  }, [editor, label]);
  // The heading is the number shown in the body ("Footnote 1"); a definition
  // without one shows its label. Fixed while open (the order may change, but a
  // heading rewriting itself reads worse).
  const heading = useMemo(() => {
    const n = footnoteOrder(editor.state.doc).get(label);
    return n === undefined ? label : String(n);
  }, [editor, label]);

  const [gone, setGone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * The inner editor's latest content (kept on every update). The write on
   * unmount (the cleanup below) cannot read the inner editor: `useEditor`'s
   * own effect was registered first, so it is destroyed first. null: nothing
   * typed, nothing to write.
   */
  const latest = useRef<JSONContent[] | null>(null);
  // The latest write and close (the editor is made once, so its callbacks call through refs).
  const write = useRef<() => void>(() => {});
  const closeRef = useRef<() => void>(() => {});

  const inner = useEditor(
    {
      extensions: [
        // The body's schema with the body's display swaps (code, toggles,
        // callouts); no footnote views and no collaboration. Pasting could put a
        // definition inside a definition; `FootnoteBlocks` does not lift it,
        // but the parse after a save does (`collectFootnoteDefs`).
        ...createEditorExtensions({
          profile: "document",
          collaboration: false,
          extensions: extensions(),
        }),
        CloseOnEscape.configure({
          onEscape: () => {
            closeRef.current();
            return true;
          },
        }),
      ],
      content: { type: "doc", content: initial },
      immediatelyRender: false,
      // Opens reading (no caret blinking right away): editing starts where the
      // text is pressed.
      autofocus: false,
      editorProps: {
        attributes: { "aria-label": m.blocks_footnote_body({ heading }) },
      },
      onUpdate: ({ editor: updated }) => {
        latest.current = (updated.getJSON().content as
          | JSONContent[]
          | undefined) ?? [{ type: "paragraph" }];
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          // Done: no longer pending (or the unmount would write it again).
          timer.current = null;
          write.current();
        }, FOOTNOTE_WRITE_DEBOUNCE_MS);
      },
    },
    [],
  );

  write.current = () => {
    // The body's editor may be gone (leaving the page unmounts both, in no set order).
    if (gone || editor.isDestroyed) return;
    const content = latest.current;
    if (content === null) return;
    const hit = findFootnoteDef(editor.state.doc, label);
    if (!hit) {
      // Removed while open: say so rather than write it somewhere.
      setGone(true);
      return;
    }
    const fragment = editor.schema.nodeFromJSON({
      type: "footnoteDef",
      attrs: hit.node.attrs,
      content,
    }).content;
    // Unchanged: no empty edit to Yjs on every close.
    if (fragment.eq(hit.node.content)) return;
    separateUndo(editor);
    editor.view.dispatch(
      editor.state.tr.replaceWith(
        hit.pos + 1,
        hit.pos + hit.node.nodeSize - 1,
        fragment,
      ),
    );
    separateUndo(editor);
  };

  // frontend-development: not a fetch; stops typing what can no longer be written.
  useEffect(() => {
    if (gone) inner?.setEditable(false);
  }, [gone, inner]);

  // Closed another way (leaving the page, the editor made again): a pending
  // write is flushed, not dropped - typing within the last 500ms would be lost.
  // A gone definition makes `write` return without writing.
  // frontend-development: not a fetch; flushes the pending write on unmount.
  useEffect(() => {
    return () => {
      if (!timer.current) return;
      clearTimeout(timer.current);
      timer.current = null;
      write.current();
    };
  }, []);

  const close = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    write.current();
    onClose();
  };
  closeRef.current = close;

  return (
    <Modal open label={m.blocks_footnote_heading({ heading })} onClose={close}>
      <div className="flex min-h-0 flex-col gap-2">
        {/* A close button: Escape and the backdrop close it too, but nothing
            on screen said so. */}
        <div className="flex items-start gap-2">
          <h2 className="m-0 min-w-0 flex-1 font-normal text-[14px] text-fg">
            {m.blocks_footnote_heading({ heading })}
          </h2>
          <button
            type="button"
            aria-label={m.blocks_close()}
            className={ICON_BUTTON}
            onClick={close}
          >
            <Icon kind="close" size={14} />
          </button>
        </div>
        {gone && (
          <p className="m-0 text-[13px] text-muted">
            {m.blocks_footnote_gone()}
          </p>
        )}
        <div className="max-h-[60vh] min-h-0 overflow-y-auto">
          <EditorContent
            editor={inner}
            className={`${DOCUMENT_BODY_CLASS} text-body`}
          />
        </div>
      </div>
    </Modal>
  );
}
